import React from 'react';
import { IonPage, IonContent, IonSpinner, IonIcon } from '@ionic/react';
import { S3_PROFILE_BASE as S3_BASE } from 'common/common';
import { chevronDownOutline, chevronForwardOutline } from 'ionicons/icons';
import { useHistory, useParams } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { SlotReduxCommandCreator } from 'appRedux/actions/slotCommandCreator';
import { ILeaderBoardList } from '@interfaces/services';
import {
  ILeaderBoardChallengeReport,
  ILeaderBoardChallengeBadges,
} from 'appRedux/models/casinoModels';
import DetailEventCard from 'pages/EventsTab/Challenges/DetailEventCard';
import { CasinoService, IChallengeItem, IChallengeInstanceEGM } from 'services/casino';
import LeaderboardTopLayer from 'pages/LeaderBoardTab/LeaderboardTopLayer';
import LeaderboardSectionTitle from 'pages/LeaderBoardTab/LeaderboardSectionTitle';
import LeaderboardCurrentUserCard from 'pages/LeaderBoardTab/LeaderboardCurrentUserCard';
import ListSheetLeaderboard from 'pages/LeaderBoardTab/ListSheetLeaderboard';
import { IState } from 'appRedux/createStore';
import { SnapPoint } from 'components/CasinoTabBar/views/home/views/MapStats/HotListReport/components/ListSheet';
import FloorMapBackdrop from '../FloorMapBackdrop';
import CasinoNavBar from 'components/CasinoNavBar';
import { buildCommonUrl } from 'pages/EventsTab/EventCard';
import { openChallengeExplore } from 'pages/EventsTab/Challenges/openChallengeExplore';
import useChallengeCountdown from 'hooks/useChallengeCountdown';
import LeaderWinnings from 'pages/LeaderBoardTab/LeaderWinnings';
import LeaderboardTop3 from 'pages/LeaderBoardTab/LeaderboardTop3';
import { IUserInfo } from 'services/auth';
import { getCasinoFolderName } from 'common/common';
import { PRIVATE_LEADERBOARD_PLAYER_NAME } from 'common/commonEnum';
import { LeaderboardPlayerName } from 'pages/LeaderBoardTab/PrivateLeaderboardPlayerLabel';

type JoinChallengeRouteParams = {
  challengeId?: string;
};

type LeaderboardRowStyle = 'gold' | 'silver' | 'bronze' | 'default' | 'highlight';
type LeaderboardMode = 'enrolled_only' | 'ranked_with_enrolled';
interface LeaderboardRowUI {
  rank: number;
  player: string;
  avatarText: string;
  avatarId?: number;
  avatarBgId?: number;
  metric: string;
  style: LeaderboardRowStyle;
  isCurrentUser: boolean;
  showLeaderboardValue?: boolean;
  hasPlayData: boolean;
  badges?: ILeaderBoardChallengeBadges;
}

interface IChallengeLeaderboardResponse extends ILeaderBoardList<ILeaderBoardChallengeReport> {
  scenarioType?: LeaderboardMode;
  frontendView?: {
    mode?: LeaderboardMode;
  };
  playedPlayerArray?: ILeaderBoardChallengeReport[];
  pendingPlayerArray?: ILeaderBoardChallengeReport[];
}

const getAvatarText = (row: ILeaderBoardChallengeReport): string => {
  const initials = (row.alias_initial2 || row.alias_initial || '').trim();
  if (initials) return initials.slice(0, 2).toUpperCase();
  const fallback = (row.alias_name || '').trim();
  return fallback ? fallback.slice(0, 2).toUpperCase() : 'P';
};

const getRankBadgeImage = (rank: number): string | null => {
  if (rank === 1) return '/assets/images/rank-gold.svg';
  if (rank === 2) return '/assets/images/rank-silver.svg';
  if (rank === 3) return '/assets/images/rank-bronze.svg';
  if (rank >= 4 && rank <= 10) return '/assets/images/rank-badge.svg';
  return null;
};

// Achievement badges are driven directly off the participant's `badges` object
// returned on each leaderboard row. mostJackpotWon is intentionally omitted for
// now (no asset yet).
const ACHIEVEMENT_BADGES: Array<{
  key: keyof ILeaderBoardChallengeBadges;
  src: string;
  alt: string;
}> = [
  { key: 'mostJackpots', src: '/assets/images/badge-jackpot.png', alt: 'Most jackpots' },
  { key: 'mostMoneyWon', src: '/assets/images/badge-money.png', alt: 'Most money won' },
  { key: 'mostMoneyWonSpin', src: '/assets/images/badge-win-spin.png', alt: 'Most money won per spin' },
  { key: 'mostSpins', src: '/assets/images/badge-spin.png', alt: 'Most spins' },
];

const RowAvatar: React.FC<{ avatarText: string; avatarId?: number; avatarBgId?: number }> = ({
  avatarText,
  avatarId,
  avatarBgId,
}) => {
  if (avatarId) {
    return (
      <span
        className="leaderboard-row__avatar leaderboard-row__avatar--img"
        style={{ backgroundImage: `url(${S3_BASE}/avatar-bg${avatarBgId}.png)` }}
      >
        <img
          src={`${S3_BASE}/avatar${avatarId}.png`}
          alt=""
          className="leaderboard-row__avatar-img"
        />
      </span>
    );
  }
  return <span className="leaderboard-row__avatar">{avatarText}</span>;
};

const AchievementBadges: React.FC<{
  badges?: ILeaderBoardChallengeBadges;
  className?: string;
}> = ({ badges, className }) => {
  if (!badges) return null;
  const active = ACHIEVEMENT_BADGES.filter((badge) => badges[badge.key]);
  if (active.length === 0) return null;
  return (
    <span className={`leaderboard-row__achievements ${className || ''}`.trim()}>
      {active.map((badge) => (
        <img
          key={badge.key}
          src={badge.src}
          alt={badge.alt}
          className="leaderboard-row__achievement"
        />
      ))}
    </span>
  );
};

const JoinChallenge: React.FC = () => {
  const { challengeId } = useParams<JoinChallengeRouteParams>();
  const history = useHistory();
  const dispatch = useDispatch();
  const slotCommands = SlotReduxCommandCreator(dispatch);
  const casinoSvcRef = React.useRef<CasinoService>(new CasinoService());
  const LEADERBOARD_PAGE_SIZE = 50;

  const casinoId = useSelector<IState, number | undefined>(
    (s) => s.app.persistedState.casinoSchema?.kpCasinoPk
  );
  const isCasinoAccount = useSelector<IState>(
    (s) => s.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;
  const isSlotcheckInternal = useSelector<IState>(
    (s) => s.app.persistedState.userInfo?.groups?.includes('SlotcheckInternal')
  ) as boolean;
  const canSeePendingDetails = isCasinoAccount || isSlotcheckInternal;
  const userInfo = useSelector<IState, unknown>((s) => s.app.persistedState.userInfo);

  const [challenge, setChallenge] = React.useState<IChallengeItem | null>(null);
  const [isChallengeLoading, setIsChallengeLoading] = React.useState(false);
  const { countdown, state: countdownState } = useChallengeCountdown(challenge);
  const [isLeaderboardLoading, setIsLeaderboardLoading] = React.useState(false);
  const [isJoining, setIsJoining] = React.useState(false);
  const [joined, setJoined] = React.useState(false);
  const [rows, setRows] = React.useState<LeaderboardRowUI[]>([]);
  const [leaderboardMode, setLeaderboardMode] = React.useState<LeaderboardMode>('enrolled_only');
  const [currentUserData, setCurrentUserData] = React.useState<ILeaderBoardChallengeReport | null>(
    null
  );
  const [pendingRows, setPendingRows] = React.useState<{ player: string; avatarText: string; avatarId?: number; avatarBgId?: number; playerId: string | null }[]>([]);
  const [expandedPendingIndices, setExpandedPendingIndices] = React.useState<Set<number>>(new Set());
  const [leaderboardPage, setLeaderboardPage] = React.useState(0);
  const [leaderboardTotalCount, setLeaderboardTotalCount] = React.useState(0);
  const [isLoadingMore, setIsLoadingMore] = React.useState(false);
  const [leaderboardLoaded, setLeaderboardLoaded] = React.useState(false);
  const [showScrollToTop, setShowScrollToTop] = React.useState(false);
  const [egmPinConfig, setEgmPinConfig] = React.useState<Record<string, { pinDisplayType: string; pinDisplayCode: string }> | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const observerRef = React.useRef<IntersectionObserver | null>(null);
  const rowsContainerRef = React.useRef<HTMLDivElement>(null);
  const challengeCardRef = React.useRef<HTMLDivElement>(null);
  const firstRowRef = React.useRef<HTMLDivElement>(null);
  const ionContentRef = React.useRef<HTMLIonContentElement>(null);

  const casinoLogoAlt = useSelector<IState>(
    (state) => state.app.persistedState.casinoSchema?.logos?.logoAlt
  ) as string | undefined;

  const userInfoRecord =
    userInfo && typeof userInfo === 'object' ? (userInfo as Record<string, unknown>) : null;
  const appUserFk =
    (typeof userInfoRecord?.appUserPk === 'string' && userInfoRecord.appUserPk) ||
    (typeof userInfoRecord?.appUserPk === 'number' ? String(userInfoRecord.appUserPk) : '');

  const fetchChallengeLeaderboard = React.useCallback(
    (page = 0, append = false) => {
      if (!casinoId || !challengeId) {
        setRows([]);
        setCurrentUserData(null);
        setIsLeaderboardLoading(false);
        return;
      }

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLeaderboardLoading(true);
      }
      const sub = casinoSvcRef.current
        .LeaderBoardChallengeListReport({
          challengeInstanceUUID: challengeId,
          casinofk: casinoId,
          appUserFk: appUserFk || undefined,
          pageNumber: page as number,
          limit: LEADERBOARD_PAGE_SIZE,
          isCasinoAccount: canSeePendingDetails || undefined,
        })
        .subscribe({
          next: (resp) => {
            const leaderboardResponse = (resp || {}) as IChallengeLeaderboardResponse;
            const inferredMode: LeaderboardMode =
              leaderboardResponse.frontendView?.mode ||
              leaderboardResponse.scenarioType ||
              ((leaderboardResponse.playedPlayerArray || []).length > 0
                ? 'ranked_with_enrolled'
                : 'enrolled_only');

            if (!append) {
              setLeaderboardMode(inferredMode);
              if (canSeePendingDetails) {
                const pendingArr = Array.isArray(leaderboardResponse.pendingPlayerArray)
                  ? leaderboardResponse.pendingPlayerArray
                  : [];
                setPendingRows(
                  pendingArr.map((row) => ({
                    player: row.alias_name || PRIVATE_LEADERBOARD_PLAYER_NAME,
                    avatarText: getAvatarText(row),
                    avatarId: row.avatarId,
                    avatarBgId: row.avatarBgId,
                    playerId: (row as ILeaderBoardChallengeReport & { playerId?: string | null }).playerId ?? null,
                  }))
                );
              }
            }

            const dataSet = Array.isArray(leaderboardResponse.DataSet)
              ? leaderboardResponse.DataSet
              : [];
            const playerData = Array.isArray(leaderboardResponse.PlayerDataSet)
              ? leaderboardResponse.PlayerDataSet
              : [];

            const hasPlayedData = (row: ILeaderBoardChallengeReport): boolean => {
              const typedRow: ILeaderBoardChallengeReport & { hasPlayData?: boolean } = row;
              if (typeof typedRow.hasPlayData === 'boolean') {
                return typedRow.hasPlayData;
              }
              return (
                Number.isFinite(Number(typedRow.orderNumber)) && Number(typedRow.orderNumber) > 0
              );
            };

            if (!append) {
              const currentUserFromDataSet = dataSet.find((row) => {
                if (!row.is_current_user) return false;
                return inferredMode === 'ranked_with_enrolled'
                  ? hasPlayedData(row)
                  : !hasPlayedData(row);
              });

              const currentUserFromPlayerData = playerData.find((row) => {
                if (!row.is_current_user) return false;
                return inferredMode === 'ranked_with_enrolled'
                  ? hasPlayedData(row)
                  : !hasPlayedData(row);
              });

              setCurrentUserData(currentUserFromDataSet || currentUserFromPlayerData || null);
            }
            if (
              typeof leaderboardResponse.totalCount !== 'undefined' &&
              leaderboardResponse.totalCount !== null
            ) {
              setLeaderboardTotalCount(Number(leaderboardResponse.totalCount));
            }
            let playedRankCounter = page * LEADERBOARD_PAGE_SIZE;
            const newRows = dataSet.map((row: ILeaderBoardChallengeReport, index: number) => {
              const typedRow: ILeaderBoardChallengeReport & { hasPlayData?: boolean } = row;
              const rawOrderNumber = Number(typedRow.orderNumber);
              const hasPlayData =
                inferredMode === 'ranked_with_enrolled'
                  ? typeof typedRow.hasPlayData === 'boolean'
                    ? Boolean(typedRow.hasPlayData)
                    : Number.isFinite(rawOrderNumber) && rawOrderNumber > 0
                  : false;

              if (hasPlayData) {
                playedRankCounter += 1;
              }

              const rank = Number(row.orderNumber || page * LEADERBOARD_PAGE_SIZE + index + 1);
              let style: 'highlight' | 'gold' | 'silver' | 'bronze' | 'default' = 'default';
              if (hasPlayData && playedRankCounter === 1) {
                style = 'gold';
              } else if (hasPlayData && playedRankCounter === 2) {
                style = 'silver';
              } else if (hasPlayData && playedRankCounter === 3) {
                style = 'bronze';
              } else if (row.is_current_user) {
                style = 'highlight';
              }
              return {
                rank,
                player: row.alias_name || PRIVATE_LEADERBOARD_PLAYER_NAME,
                avatarText: getAvatarText(row),
                avatarId: row.avatarId,
                avatarBgId: row.avatarBgId,
                metric: hasPlayData ? `${Math.round(Number(row.value || 0)).toLocaleString()}` : '',
                style,
                isCurrentUser: Boolean(row.is_current_user),
                showLeaderboardValue: hasPlayData ? row.showValueInLeaderboard : false,
                hasPlayData,
                badges: row.badges,
              };
            });
            if (append) {
              setRows((prev) => [...prev, ...newRows]);
            } else {
              setRows(newRows);
              setLeaderboardLoaded(true);
            }
          },
          error: () => {
            setIsLoadingMore(false);
            setRows([]);
            setCurrentUserData(null);
            setIsLeaderboardLoading(false);
          },
          complete: () => {
            setIsLeaderboardLoading(false);
            setIsLoadingMore(false);
          },
        });

      return sub;
    },
    [casinoId, challengeId, appUserFk]
  );

  const hasMore = rows.length < leaderboardTotalCount;
  const rankedRows = React.useMemo(() => rows.filter((row) => row.hasPlayData), [rows]);
  const enrolledRows = React.useMemo(() => rows.filter((row) => !row.hasPlayData), [rows]);
  const isRankedMode = leaderboardMode === 'ranked_with_enrolled' && rankedRows.length > 0;
  const showValue = Boolean(rankedRows[0]?.showLeaderboardValue);
  const isGameEnded = Boolean(challenge?.endDate && new Date() > new Date(challenge.endDate));

  const challengeHeaderBgUrl = React.useMemo(() => {
    if (!challenge) return undefined;
    const exploreHeaderStyle = challenge?.details?.exploreHeaderStlye;
    if (!exploreHeaderStyle) return undefined;
    return buildCommonUrl('assets/images/headers', exploreHeaderStyle);
  }, [challenge]);

  const { aliasInitial2, aliasName } = useSelector<IState>(
    (state) => state.app.persistedState.userInfo
  ) as IUserInfo;

  const handleLoadMore = React.useCallback(() => {
    if (isLoadingMore || isLeaderboardLoading || !hasMore) return;
    const nextPage = leaderboardPage + 1;
    setLeaderboardPage(nextPage);
    fetchChallengeLeaderboard(nextPage, true);
  }, [isLoadingMore, isLeaderboardLoading, hasMore, leaderboardPage, fetchChallengeLeaderboard]);

  React.useEffect(() => {
    if (!casinoId || !challengeId) {
      setChallenge(null);
      setIsChallengeLoading(false);
      return;
    }

    setIsChallengeLoading(true);
    const sub = casinoSvcRef.current.GetChallengeById(casinoId, challengeId).subscribe({
      next: (challengeData) => {
        setChallenge(challengeData);
        setJoined(Boolean(challengeData?.joined));
      },
      error: () => {
        setChallenge(null);
      },
      complete: () => setIsChallengeLoading(false),
    });

    const egmSub = casinoSvcRef.current
      .GetChallengeInstanceEGMs(challengeId, casinoId)
      .subscribe(
        (egms) => {
          if (!Array.isArray(egms)) return;
          const config: Record<string, { pinDisplayType: string; pinDisplayCode: string }> = {};
          egms.forEach((egm) => {
            if (egm.machineId && egm.pinDisplayType && egm.pinDisplayCode) {
              config[String(egm.machineId)] = {
                pinDisplayType: egm.pinDisplayType,
                pinDisplayCode: egm.pinDisplayCode,
              };
            }
          });
          setEgmPinConfig(Object.keys(config).length > 0 ? config : null);
        }
      );

    return () => {
      sub.unsubscribe();
      egmSub.unsubscribe();
    };
  }, [casinoId, challengeId]);

  React.useEffect(() => {
    setLeaderboardPage(0);
    setLeaderboardTotalCount(0);
    setRows([]);
    setPendingRows([]);
    setExpandedPendingIndices(new Set());
    setLeaderboardLoaded(false);
    const leaderboardSub = fetchChallengeLeaderboard(0, false);
    return () => leaderboardSub?.unsubscribe();
  }, [fetchChallengeLeaderboard]);

  // IntersectionObserver to trigger load-more when sentinel comes into view
  React.useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }
    if (!hasMore || isLoadingMore || isLeaderboardLoading) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );
    if (sentinelRef.current) {
      observerRef.current.observe(sentinelRef.current);
    }
    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasMore, isLoadingMore, isLeaderboardLoading, handleLoadMore]);

  // Full leaderboard slam animation:
  //  • ALL rows fly in via position:fixed (escapes overflow:hidden clipping)
  //  • Rows stagger 55ms apart, bottom-to-top order (build-up to #1)
  //  • Each row self-shakes on landing
  //  • Top-3 get 10% stronger fly-in scale
  //  • #1, #2, #3 all fly in simultaneously
  //  • All slam at the same moment
  //  • Shake ripples: #1 + card first, then rolls down through all other rows
  React.useEffect(() => {
    if (!leaderboardLoaded || rows.length === 0 || isLeaderboardLoading) return;
    const container = rowsContainerRef.current;
    if (!container) return;

    const timeouts: ReturnType<typeof setTimeout>[] = [];
    let rafId: number;
    let cancelled = false;

    rafId = requestAnimationFrame(() => {
      if (cancelled) return;

      const allRowEls = Array.from(container.querySelectorAll<HTMLElement>('.leaderboard-row'));
      if (allRowEls.length === 0) return;

      // Helper: apply shake inline
      const shakeEl = (el: HTMLElement, amp: number, dur: number) => {
        el.style.setProperty('--shake-amp', `${amp}px`);
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.animation = `leaderboard-wave-shake ${dur}ms cubic-bezier(0.36, 0.07, 0.19, 0.97) both`;
        const t = setTimeout(() => {
          el.style.animation = '';
          el.style.removeProperty('--shake-amp');
        }, dur + 60);
        timeouts.push(t);
      };

      const FLY_DUR = 450; // all 3 fly simultaneously, same duration
      // #1 slightly bigger scale (6.5x), #2=6.0x, #3=5.5x for depth difference
      const top3 = [
        { el: allRowEls[0], scale: 6.5 }, // rank 1
        { el: allRowEls[1], scale: 6.0 }, // rank 2
        { el: allRowEls[2], scale: 5.5 }, // rank 3
      ].filter(({ el }) => !!el);

      // --- All 3 fly in at once ---
      top3.forEach(({ el, scale }) => {
        const rect = el.getBoundingClientRect();
        el.style.position = 'fixed';
        el.style.top = `${rect.top}px`;
        el.style.left = `${rect.left}px`;
        el.style.width = `${rect.width}px`;
        el.style.zIndex = '9999';
        el.style.margin = '0';
        el.style.setProperty('--fly-scale', String(scale));
        void el.offsetWidth;
        el.style.animation = `leaderboard-fly-in ${FLY_DUR}ms cubic-bezier(0.08, 0.92, 0.20, 1.06) both`;
      });

      // --- All slam at the same time: restore all 3, then shake ripple ---
      const slamT = setTimeout(() => {
        // Restore all 3 to normal flow
        top3.forEach(({ el }) => {
          el.style.position = '';
          el.style.top = '';
          el.style.left = '';
          el.style.width = '';
          el.style.zIndex = '';
          el.style.margin = '';
          el.style.animation = '';
          el.style.removeProperty('--fly-scale');
        });

        // Shake ripple: #1 + challenge card first (0ms delay),
        // then ripple down through rows 2, 3, 4... with 60ms stagger each
        // #1 shakes biggest, rest get random amplitude

        // Challenge card — fires with #1 (0ms)
        if (challengeCardRef.current) {
          const cardEl = challengeCardRef.current;
          shakeEl(cardEl, 20, 650);
        }

        // #1 row — fires first, biggest shake
        if (allRowEls[0]) {
          shakeEl(allRowEls[0], 28, 750);
        }

        // Rows 2 onward ripple down with 60ms stagger each
        for (let i = 1; i < allRowEls.length; i++) {
          const delay = i * 60; // 60ms per row, rolling down the list
          const amp = 8 + Math.floor(Math.random() * 14); // random 8-22px
          const dur = 400 + Math.floor(Math.random() * 150);
          const t = setTimeout(() => shakeEl(allRowEls[i], amp, dur), delay);
          timeouts.push(t);
        }

        // #1 shakes biggest — no zoom-pop (was causing double-motion confusion)
      }, FLY_DUR);
      timeouts.push(slamT);
    }); // end rAF

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      timeouts.forEach(clearTimeout);
      const rowEls = Array.from(container.querySelectorAll<HTMLElement>('.leaderboard-row'));
      rowEls.forEach((el) => {
        el.style.position = '';
        el.style.top = '';
        el.style.left = '';
        el.style.width = '';
        el.style.zIndex = '';
        el.style.margin = '';
        el.style.animation = '';
        el.style.removeProperty('--fly-scale');
        el.style.removeProperty('--shake-amp');
      });
      if (challengeCardRef.current) {
        challengeCardRef.current.style.animation = '';
        challengeCardRef.current.style.removeProperty('--shake-amp');
      }
    };
  }, [leaderboardLoaded, rows.length, isLeaderboardLoading]);

  const scrollToTop = React.useCallback(() => {
    const panelContent = document.querySelector('.panel-content') as HTMLElement | null;
    if (panelContent) {
      panelContent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  React.useEffect(() => {
    const panelContent = document.querySelector('.panel-content') as HTMLElement | null;
    if (!panelContent) return;

    const onScroll = () => {
      const el = firstRowRef.current;
      if (!el) { setShowScrollToTop(false); return; }
      const elRect = el.getBoundingClientRect();
      const containerRect = panelContent.getBoundingClientRect();
      setShowScrollToTop(elRect.bottom < containerRect.top);
    };

    panelContent.addEventListener('scroll', onScroll, { passive: true });
    return () => panelContent.removeEventListener('scroll', onScroll);
  }, [rows]);

  const handleJoin = () => {
    if (!challenge || !casinoId) return;

    const challengeInstanceUUID = String(challenge.uuid || challenge._id || '');
    if (!challengeInstanceUUID) return;

    const nestedUser =
      userInfoRecord?.user && typeof userInfoRecord.user === 'object'
        ? (userInfoRecord.user as Record<string, unknown>)
        : null;

    const cognitoUserName =
      (typeof nestedUser?.cognitoUserName === 'string' && nestedUser.cognitoUserName) ||
      (typeof userInfoRecord?.cognitoUserName === 'string' && userInfoRecord.cognitoUserName) ||
      appUserFk;

    if (!cognitoUserName) {
      history.push('/auth/login');
      return;
    }

    setIsJoining(true);
    casinoSvcRef.current.EnrollChallengeInstance(challengeInstanceUUID, casinoId).subscribe({
      next: () => {
        setJoined(true);
        setLeaderboardPage(0);
        setLeaderboardTotalCount(0);
        setRows([]);
        fetchChallengeLeaderboard(0, false);
      },
      error: () => setIsJoining(false),
      complete: () => setIsJoining(false),
    });
  };

  const openFeaturedChallenge = () => {
    openChallengeExplore(slotCommands, history, challenge, egmPinConfig, casinoId);
  };

  return (
    <IonPage className="join-challenge-page events-tab">
      <IonContent ref={ionContentRef} className="leaderboard-tab__content events-tab__content" fullscreen>
        <CasinoNavBar headerBackgroundUrl={challengeHeaderBgUrl} />

        <FloorMapBackdrop />

        <ListSheetLeaderboard initialSnap={SnapPoint.Full}>
          <div>
            <LeaderboardTopLayer
              title={'Challenges'}
              showBack
              onBack={() => history.goBack()}
              showBadge
              countdown={countdown}
              countdownState={countdownState}
              showTimer={true}
              badgeIcon="challenge"
            />
            <div>
              {isChallengeLoading && (
                <div
                  className="leaderboard-mapstats__loader leaderboard-mapstats__loader--challenge"
                  role="status"
                  aria-live="polite"
                >
                  <span className="leaderboard-table__spinner" aria-hidden="true" />
                  <span>Loading challenge...</span>
                </div>
              )}

              {/* {!isChallengeLoading && challenge && countdownState !== 'none' && (
                <div className="events-hero__tabs">
                  <span className="events-hero__tab events-hero__tab--active">
                    {countdownState === 'upcoming' ? 'Begins in' : 'Ends in'}
                  </span>
                  <span className="events-hero__timer">{countdown}</span>
                </div>
              )} */}

              {!isChallengeLoading && challenge && (
                <>
                  <div
                    ref={challengeCardRef}
                    className="challenge-card-wrapper"
                    style={countdownState === 'none' ? { marginTop: 5 } : undefined}
                  >
                    <DetailEventCard
                      title={
                        <>
                          {challenge.name}
                          {/* <br />
                        {challenge.valueLabel ?? ''} */}
                        </>
                      }
                      subtitle={challenge.description}
                      descriptions={challenge.descriptions}
                      justJoined={joined}
                      onCtaClick={!canSeePendingDetails && !(challenge.joined || joined) ? handleJoin : undefined}
                      onExploreClick={openFeaturedChallenge}
                      joined={challenge.joined || joined}
                      casinoLogoSrc={casinoLogoAlt}
                      styleData={challenge?.challenge?.styleData}
                      participantStatus={challenge.joined && !joined ? 'enrolled' : joined ? undefined : 'pending'}
                      viewableDate={challenge.endDate}
                      endDate={challenge.endDate}
                    />
                  </div>
                  {/* <div className="events-explore">
                    <EventsFeatureActionRow onExploreClick={openFeaturedChallenge} showText />
                  </div> */}
                </>
              )}

              {!isChallengeLoading && !challenge && (
                <div className="challenges-empty" style={{ marginTop: 24 }}>
                  Challenge not found.
                </div>
              )}


              {!isChallengeLoading && challenge && isGameEnded && isRankedMode && rankedRows.length > 0 && (
                <LeaderboardTop3
                  first={rankedRows[0] ? { avatarText: rankedRows[0].avatarText, name: rankedRows[0].player, value: showValue ? rankedRows[0].metric : undefined, isCurrentUser: rankedRows[0].isCurrentUser } : undefined}
                  second={rankedRows[1] ? { avatarText: rankedRows[1].avatarText, name: rankedRows[1].player, value: showValue ? rankedRows[1].metric : undefined, isCurrentUser: rankedRows[1].isCurrentUser } : undefined}
                  third={rankedRows[2] ? { avatarText: rankedRows[2].avatarText, name: rankedRows[2].player, value: showValue ? rankedRows[2].metric : undefined, isCurrentUser: rankedRows[2].isCurrentUser } : undefined}
                />
              )}

              {!isChallengeLoading && challenge && challenge?.winning && (() => {
                const details = { ...challenge.challenge?.details, ...challenge.details };
                return (
                  <LeaderWinnings
                    amount={challenge.winning}
                    title={challenge.name}
                    subtitle={details.subtitle}
                    rewards={details.rewards || []}
                    bonusTitle={details.bonusTitle}
                    bonusSubtitle={details.bonusSubtitle}
                    footerNote={details.footerNote}
                    notice={details.notice}
                    dateRange={details.dateRange}
                    bonusRewards={details.bonusRewards}
                    styleBackground={challenge?.challenge?.styleData?.background}
                    styleName={getCasinoFolderName(casinoId)}
                  />
                );
              })()}

              {!isLeaderboardLoading && !canSeePendingDetails && challenge && joined && (
                <LeaderboardCurrentUserCard
                  player={aliasName || ''}
                  avatarText={aliasInitial2 || ''}
                  rank={currentUserData ? Number(currentUserData.orderNumber) : 0}
                  metricLabel="Points"
                  metricValue={
                    currentUserData
                      ? `${Math.round(Number(currentUserData.value)).toLocaleString()}`
                      : '0'
                  }
                />
              )}

              {!isChallengeLoading && challenge && (
                <div className="leaderboard-section" style={{ marginTop: 16 }}>
                  <div className="leaderboard-section__header">
                    {!isLeaderboardLoading && (
                      <LeaderboardSectionTitle
                        title={countdownState === 'upcoming' ? 'Enrolled' : 'Leaderboard'}
                        titleClassName="leaderboard-header-title"
                      />
                    )}
                    {showScrollToTop && (
                      <button className="leaderboard-scroll-top" onClick={scrollToTop}>
                        <span className="leaderboard-scroll-top__arrow">▲</span>
                        To Top
                      </button>
                    )}
                  </div>

                  

                  <div
                    className={`leaderboard-table${
                      isRankedMode && !isLeaderboardLoading && rows.length > 0 && !showValue
                        ? ' leaderboard-table--hide-left-rank'
                        : ''
                    }`}
                  >
                    {!isLeaderboardLoading && rows.length > 0 && isRankedMode && (
                      <>
                        <div className="leaderboard-table__header">
                          {showValue && <span>Rank</span>}
                          <span>Players</span>
                          <span className="leaderboard-table__header-metric">
                            {showValue ? challenge?.valueLabel || 'Value' : 'Rank'}
                          </span>
                          <span className="leaderboard-table__header-badge" aria-hidden="true" />
                        </div>
                      </>
                    )}

                    {!isLeaderboardLoading && rows.length > 0 && !isRankedMode && (
                      <div className="leaderboard-table__section-label">Enrolled</div>
                    )}

                    <div className="leaderboard-table__rows" ref={rowsContainerRef}>
                      {isLeaderboardLoading ? (
                        <div className="leaderboard-table__state leaderboard-table__state--loading">
                          <span className="leaderboard-table__spinner" aria-hidden="true" />
                          <span>Loading leaderboard...</span>
                        </div>
                      ) : rows.length === 0 ? (
                        <div className="leaderboard-table__state">On your mark. Get set….</div>
                      ) : (
                        <>
                          {(isRankedMode ? rankedRows : enrolledRows).map((row, idx) => {
                            const badgeImage = getRankBadgeImage(row.rank);
                            const hasTopThreeBackground =
                              row.isCurrentUser &&
                              (row.style === 'gold' ||
                                row.style === 'silver' ||
                                row.style === 'bronze');

                            // Fly-in handled entirely by JS useEffect; no static slam class needed
                            const slamRankClass = '';

                            return (
                              <div
                                key={`ranked-${row.rank}-${row.player}`}
                                ref={idx === 0 ? firstRowRef : undefined}
                                className={`leaderboard-row leaderboard-row--${row.style} ${
                                  row.isCurrentUser ? 'leaderboard-row--currentuser' : ''
                                } ${slamRankClass}`}
                              >
                                {hasTopThreeBackground && (
                                  <div className="leaderboard-row__top3-fill" aria-hidden="true" />
                                )}
                                {showValue && (
                                  <div className="leaderboard-row__rank">{row.rank}</div>
                                )}
                                <div className="leaderboard-row__player">
                                  <RowAvatar avatarText={row.avatarText} avatarId={row.avatarId} avatarBgId={row.avatarBgId} />
                                  <LeaderboardPlayerName
                                    name={row.player}
                                    className="leaderboard-row__player-name"
                                  />
                                </div>

                                {isRankedMode ? (
                                  <>
                                    <div className="leaderboard-row__won">
                                      <AchievementBadges badges={row.badges} />
                                      <span className="leaderboard-row__won-value">
                                        {row.showLeaderboardValue ? row.metric : row.rank}
                                      </span>
                                    </div>
                                    <div className="leaderboard-row__badge">
                                      {badgeImage && (
                                        <img
                                          src={badgeImage}
                                          alt={`Rank ${row.rank} badge`}
                                          className="leaderboard-row__badge-image"
                                        />
                                      )}
                                      {row.rank >= 4 && row.rank <= 10 && (
                                        <span
                                          className="leaderboard-row__badge-rank"
                                          aria-hidden="true"
                                        >
                                          {row.rank}
                                        </span>
                                      )}
                                    </div>
                                  </>
                                ) : (
                                  <AchievementBadges
                                    badges={row.badges}
                                    className="leaderboard-row__achievements--standalone"
                                  />
                                )}
                              </div>
                            );
                          })}

                          {isRankedMode && enrolledRows.length > 0 && (
                            <div className="leaderboard-table__section-label leaderboard-table__section-label--divider">
                              Enrolled
                            </div>
                          )}

                          {isRankedMode &&
                            enrolledRows.map((row, index) => {
                              const fallbackRank = rankedRows.length + index + 1;
                              return (
                                <div
                                  key={`enrolled-${fallbackRank}-${row.player}`}
                                  className={`leaderboard-row leaderboard-row--default ${
                                    row.isCurrentUser ? 'leaderboard-row--currentuser' : ''
                                  }`}
                                  style={{ gridTemplateColumns: '1fr auto', padding: '10px 12px' }}
                                >
                                  <div
                                    className="leaderboard-row__player"
                                    style={{ overflow: 'visible' }}
                                  >
                                    <RowAvatar avatarText={row.avatarText} avatarId={row.avatarId} avatarBgId={row.avatarBgId} />
                                    <LeaderboardPlayerName
                                      name={row.player}
                                      className="leaderboard-row__player-name"
                                    />
                                  </div>
                                  <AchievementBadges
                                    badges={row.badges}
                                    className="leaderboard-row__achievements--standalone"
                                  />
                                </div>
                              );
                            })}

                          {canSeePendingDetails && pendingRows.length > 0 && (
                            <div className="leaderboard-table__section-label leaderboard-table__section-label--divider">
                              Pending Enrollment
                            </div>
                          )}

                          {canSeePendingDetails &&
                            pendingRows.map((row, index) => {
                              const isExpanded = expandedPendingIndices.has(index);
                              return (
                                <div key={`pending-${index}-${row.player}`}>
                                  <div
                                    className={`leaderboard-row leaderboard-row--default${isExpanded ? ' leaderboard-row--pending-expanded' : ''}`}
                                    style={{ gridTemplateColumns: '1fr auto', padding: '10px 12px', cursor: 'pointer' }}
                                    onClick={() => {
                                      setExpandedPendingIndices((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(index)) {
                                          next.delete(index);
                                        } else {
                                          next.add(index);
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    <div className="leaderboard-row__player" style={{ overflow: 'visible' }}>
                                      <RowAvatar avatarText={row.avatarText} avatarId={row.avatarId} avatarBgId={row.avatarBgId} />
                                      <LeaderboardPlayerName
                                        name={row.player}
                                        className="leaderboard-row__player-name"
                                      />
                                    </div>
                                    <IonIcon
                                      icon={isExpanded ? chevronDownOutline : chevronForwardOutline}
                                      className="leaderboard-row__pending-chevron"
                                    />
                                  </div>
                                  {isExpanded && (
                                    <div className="leaderboard-row__pending-detail">
                                      <span className="leaderboard-row__pending-detail-label">Player ID:</span>
                                      <span className="leaderboard-row__pending-detail-value">
                                        {row.playerId ?? '—'}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </>
                      )}
                      {/* Sentinel for infinite scroll load-more */}
                      {!isLeaderboardLoading && rows.length > 0 && (
                        <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />
                      )}
                      {isLoadingMore && (
                        <div
                          className="leaderboard-table__state leaderboard-table__state--loading"
                          style={{ padding: '12px 0' }}
                        >
                          <IonSpinner name="bubbles" style={{ width: 24, height: 24 }} />
                          <span style={{ marginLeft: 8 }}>Loading more...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ListSheetLeaderboard>
      </IonContent>
    </IonPage>
  );
};

export default JoinChallenge;
